import { notify } from '@billy/design-system'
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FileListItemType } from '@views/documents/elements/FileList'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { Scope } from '../../../enums/scope'
import { isAuthorized } from '../../../utils/isAuthorized'
import { useMoveDocumentOrFolder } from '../elements/MoveItemModal/hooks/useMoveDocumentOrFolder'
import { useFetchFolderTree } from '../hooks/useFetchFolderTree'

interface DraggedDocumentItem {
  id: string
  sourceFolderId: string
  type: FileListItemType
}

interface DocumentsDragAndDropContextValue {
  draggedItem: DraggedDocumentItem | null
  isMoving: boolean
  moveDraggedItemToFolder: (targetFolderId: string) => void
  startDraggingItem: (item: DraggedDocumentItem) => void
  stopDraggingItem: () => void
}

const DocumentsDragAndDropContext = createContext<DocumentsDragAndDropContextValue | undefined>(undefined)

interface DocumentsDragAndDropProviderProps {
  children: ReactNode
}

export const DocumentsDragAndDropProvider = ({ children }: DocumentsDragAndDropProviderProps) => {
  const { t } = useTranslation()
  const [draggedItem, setDraggedItem] = useState<DraggedDocumentItem | null>(null)
  const { tree } = useFetchFolderTree()

  const { moveItems, isLoading: isMoving } = useMoveDocumentOrFolder({
    onSuccess: () => {
      notify({
        id: NotificationKeys.DocumentsMoveItem,
        message: t('documents.modal.move_item.notification.success'),
        variant: 'success',
      })
    },
    onError: () => {
      notify({
        id: NotificationKeys.DocumentsMoveItem,
        message: t('documents.modal.move_item.notification.error'),
        variant: 'error',
      })
    },
  })

  const startDraggingItem = useCallback((item: DraggedDocumentItem) => {
    setDraggedItem(item)
  }, [])

  const stopDraggingItem = useCallback(() => {
    setDraggedItem(null)
  }, [])

  const isDescendantFolder = useCallback(
    (candidateId: string, ancestorId: string): boolean => {
      const nodesById = Object.fromEntries((tree ?? []).map((node) => [node.id, node]))
      let current = nodesById[candidateId]
      while (current?.parentId) {
        if (current.parentId === ancestorId) {
          return true
        }
        current = nodesById[current.parentId]
      }
      return false
    },
    [tree],
  )

  const moveDraggedItemToFolder = useCallback(
    (targetFolderId: string) => {
      if (!draggedItem || draggedItem.id === targetFolderId || draggedItem.sourceFolderId === targetFolderId) {
        setDraggedItem(null)
        return
      }

      if (draggedItem.type === 'folder') {
        if (!isAuthorized(Scope.DmsFolderWrite)) {
          setDraggedItem(null)
          return
        }

        // Require tree to be loaded before allowing folder moves, matching FolderTree.canDropOnNode()
        if (!tree || tree.length === 0) {
          setDraggedItem(null)
          return
        }
        if (isDescendantFolder(targetFolderId, draggedItem.id)) {
          setDraggedItem(null)
          return
        }
      }

      moveItems({
        items: [{ id: draggedItem.id, type: draggedItem.type }],
        parentFolderId: targetFolderId,
        sourceFolderId: draggedItem.sourceFolderId,
      })
      setDraggedItem(null)
    },
    [draggedItem, isDescendantFolder, moveItems, tree],
  )

  const value = useMemo(
    () => ({ draggedItem, isMoving, moveDraggedItemToFolder, startDraggingItem, stopDraggingItem }),
    [draggedItem, isMoving, moveDraggedItemToFolder, startDraggingItem, stopDraggingItem],
  )

  return <DocumentsDragAndDropContext.Provider value={value}>{children}</DocumentsDragAndDropContext.Provider>
}

export const useDocumentsDragAndDrop = (): DocumentsDragAndDropContextValue => {
  const context = useContext(DocumentsDragAndDropContext)

  if (!context) {
    throw new Error('useDocumentsDragAndDrop must be used within a DocumentsDragAndDropProvider')
  }

  return context
}
